export type SharedRoomStatus = "lobby" | "ready" | "playing" | "reveal" | "rest" | "ended" | "expired";
export type SharedRoomEventType =
  | "PARTICIPANT_JOINED"
  | "PARTICIPANT_DISCONNECTED"
  | "PARTICIPANT_RECONNECTED"
  | "PARTICIPANT_READY"
  | "QUEUE_UPDATED"
  | "ROUND_STARTED"
  | "ROUND_COMPLETED"
  | "ROUND_SKIPPED"
  | "REST_DEFERRED"
  | "REST_COMPLETED"
  | "SESSION_ENDED";

export type SharedRoomParticipant = {
  id: string;
  joinedAt: string;
  connectedAt: string;
  disconnectedAt: string | null;
  ready: boolean;
};

export type SharedRoomEvent = {
  eventId: string;
  expectedRevision: number;
  type: SharedRoomEventType;
  actorId: string;
  payload: Record<string, unknown>;
};

export type SharedRoomReceipt = {
  eventId: string;
  sequence: number;
  revision: number;
  type: SharedRoomEventType;
  serverTime: string;
};

export type SharedRoomSnapshot = {
  roomId: string;
  status: SharedRoomStatus;
  revision: number;
  sequence: number;
  createdAt: string;
  expiresAt: string;
  hostParticipantId: string;
  hostDisconnectedAt: string | null;
  participants: SharedRoomParticipant[];
  receipts: SharedRoomReceipt[];
  events: Array<SharedRoomReceipt & { payload: Record<string, unknown> }>;
};

export type SharedRoomApplyResult =
  | { ok: true; duplicate: boolean; snapshot: SharedRoomSnapshot; receipt: SharedRoomReceipt }
  | { ok: false; error: "expired" | "revision_conflict" | "forbidden" | "invalid_transition" | "unsafe_payload" };

const HOST_GRACE_MS = 30_000;
const sensitivePayloadKey = /answer|nickname|role|drink|amount|contact|roomcode|query/i;
const hostOnly = new Set<SharedRoomEventType>([
  "QUEUE_UPDATED", "ROUND_STARTED", "ROUND_COMPLETED", "ROUND_SKIPPED",
  "REST_DEFERRED", "REST_COMPLETED", "SESSION_ENDED",
]);

function safePayload(payload: Record<string, unknown>) {
  return Object.entries(payload).every(([key, value]) => {
    if (sensitivePayloadKey.test(key)) return false;
    if (typeof value === "string" && value.length > 160) return false;
    if (Array.isArray(value) && value.length > 20) return false;
    return value === null || ["string", "number", "boolean"].includes(typeof value) ||
      (Array.isArray(value) && value.every((item) => ["string", "number", "boolean"].includes(typeof item)));
  });
}

export function createSharedRoom(input: {
  roomId: string;
  hostParticipantId: string;
  now: string;
  ttlMs?: number;
}): SharedRoomSnapshot {
  const ttlMs = input.ttlMs ?? 6 * 60 * 60 * 1_000;
  return {
    roomId: input.roomId,
    status: "lobby",
    revision: 0,
    sequence: 0,
    createdAt: input.now,
    expiresAt: new Date(Date.parse(input.now) + ttlMs).toISOString(),
    hostParticipantId: input.hostParticipantId,
    hostDisconnectedAt: null,
    participants: [{ id: input.hostParticipantId, joinedAt: input.now, connectedAt: input.now, disconnectedAt: null, ready:false }],
    receipts: [],
    events: [],
  };
}

export function advanceSharedRoomClock(snapshot: SharedRoomSnapshot, now: string): SharedRoomSnapshot {
  const nowMs = Date.parse(now);
  if (snapshot.status === "expired" || nowMs >= Date.parse(snapshot.expiresAt)) {
    return { ...snapshot, status: "expired", participants: [], receipts: [], events: [], hostDisconnectedAt: null };
  }
  if (!snapshot.hostDisconnectedAt || nowMs - Date.parse(snapshot.hostDisconnectedAt) < HOST_GRACE_MS) return snapshot;
  const nextHost = snapshot.participants
    .filter((participant) => participant.disconnectedAt === null && participant.id !== snapshot.hostParticipantId)
    .sort((left, right) => left.joinedAt.localeCompare(right.joinedAt) || left.id.localeCompare(right.id))[0];
  return nextHost ? { ...snapshot, hostParticipantId: nextHost.id, hostDisconnectedAt: null } : snapshot;
}

function nextStatus(status: SharedRoomStatus, type: SharedRoomEventType): SharedRoomStatus | null {
  if (type === "PARTICIPANT_READY") return ["lobby","ready"].includes(status) ? status : null;
  if (type === "PARTICIPANT_JOINED" || type === "PARTICIPANT_DISCONNECTED" || type === "PARTICIPANT_RECONNECTED" || type === "QUEUE_UPDATED") return status;
  if (type === "SESSION_ENDED") return "ended";
  if (type === "ROUND_STARTED" && ["lobby", "ready", "reveal", "rest"].includes(status)) return "playing";
  if ((type === "ROUND_COMPLETED" || type === "ROUND_SKIPPED") && status === "playing") return "reveal";
  if (type === "REST_DEFERRED" && status === "rest") return "ready";
  if (type === "REST_COMPLETED" && status === "rest") return "ready";
  return null;
}

export function applySharedRoomEvent(rawSnapshot: SharedRoomSnapshot, event: SharedRoomEvent, now: string): SharedRoomApplyResult {
  const snapshot = advanceSharedRoomClock(rawSnapshot, now);
  if (snapshot.status === "expired" || snapshot.status === "ended") return { ok: false, error: "expired" };
  const previous = snapshot.receipts.find((receipt) => receipt.eventId === event.eventId);
  if (previous) return { ok: true, duplicate: true, snapshot, receipt: previous };
  if (event.expectedRevision !== snapshot.revision) return { ok: false, error: "revision_conflict" };
  if (!safePayload(event.payload)) return { ok: false, error: "unsafe_payload" };
  if (hostOnly.has(event.type) && event.actorId !== snapshot.hostParticipantId) return { ok: false, error: "forbidden" };

  let status = nextStatus(snapshot.status, event.type);
  if (!status) return { ok: false, error: "invalid_transition" };
  let participants = snapshot.participants;
  let hostDisconnectedAt = snapshot.hostDisconnectedAt;
  if (event.type === "PARTICIPANT_JOINED") {
    if (participants.some((participant) => participant.id === event.actorId)) return { ok: false, error: "invalid_transition" };
    participants = [...participants, { id: event.actorId, joinedAt: now, connectedAt: now, disconnectedAt: null, ready:false }];
    if (["lobby", "ready"].includes(status)) status = "lobby";
  } else if (event.type === "PARTICIPANT_DISCONNECTED") {
    participants = participants.map((participant) => participant.id === event.actorId ? { ...participant, disconnectedAt: now, ready:false } : participant);
    if (event.actorId === snapshot.hostParticipantId) hostDisconnectedAt = now;
    if (["lobby", "ready"].includes(status)) status = "lobby";
  } else if (event.type === "PARTICIPANT_RECONNECTED") {
    participants = participants.map((participant) => participant.id === event.actorId ? { ...participant, connectedAt: now, disconnectedAt: null } : participant);
    if (event.actorId === snapshot.hostParticipantId) hostDisconnectedAt = null;
    if (["lobby", "ready"].includes(status)) status = "lobby";
  } else if (event.type === "PARTICIPANT_READY") {
    if (typeof event.payload.ready !== "boolean" || !participants.some((participant) => participant.id === event.actorId)) return { ok:false, error:"invalid_transition" };
    participants = participants.map((participant) => participant.id === event.actorId ? { ...participant, ready:event.payload.ready as boolean } : participant);
    status = participants.length >= 2 && participants.every((participant) => participant.disconnectedAt === null && participant.ready) ? "ready" : "lobby";
  } else if (event.type === "QUEUE_UPDATED") {
    participants = participants.map((participant) => ({ ...participant, ready:false }));
    status = "lobby";
  } else if (event.type === "ROUND_STARTED") {
    participants = participants.map((participant) => ({ ...participant, ready:false }));
  }

  const receipt: SharedRoomReceipt = {
    eventId: event.eventId,
    sequence: snapshot.sequence + 1,
    revision: snapshot.revision + 1,
    type: event.type,
    serverTime: now,
  };
  const next = {
    ...snapshot,
    status,
    sequence: receipt.sequence,
    revision: receipt.revision,
    participants,
    hostDisconnectedAt,
    receipts: [...snapshot.receipts.slice(-99), receipt],
    events: [...snapshot.events.slice(-49), { ...receipt, payload: event.payload }],
  };
  return { ok: true, duplicate: false, snapshot: next, receipt };
}

export function replaySharedRoomEvents(snapshot: SharedRoomSnapshot, afterSequence: number) {
  return snapshot.events.filter((event) => event.sequence > afterSequence);
}

export function consumeRateLimit(input: {
  timestamps: number[];
  now: number;
  windowMs: number;
  limit: number;
}): { allowed: boolean; timestamps: number[]; retryAfterMs: number } {
  const active = input.timestamps.filter((timestamp) => input.now - timestamp < input.windowMs).sort((a, b) => a - b);
  if (active.length >= input.limit) return { allowed: false, timestamps: active, retryAfterMs: Math.max(1, input.windowMs - (input.now - active[0]!)) };
  return { allowed: true, timestamps: [...active, input.now], retryAfterMs: 0 };
}
